import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { ArrowRight, ChevronRight } from 'lucide-react';
import queryString from 'utils/queryString';
import productStillImages from 'utils/productStillImages';
import {
  HtmlContent,
  ProductDetailCarousel,
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  ScriptMarkupProduct,
  faqContentToItems,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import { formatPrice } from 'utils/format';
import { getSkuAttributes } from 'data/lib/productReducer';
import useCartContext from 'ecommerce/context/cart';
import { prepareBundleData } from 'ecommerce/components/productConfiguration';
import { getInitialPriceData } from 'ecommerce/lib/productConfiguration';
import useEditorContext from 'editor/context/editorContext';

import { CartPageLink, EditorPageLink } from '../pageLinks';
import { Head, Layout } from '../layout';
import SETTINGS from '../../settings';
import tracker from '../../lib/tracker';

import ProductPicker from './productPicker';
import StickyProductBar from './stickyProductBar';
import {
  SECTION_IDS,
  Icon,
  InsideBook,
  InsideBookTeaser,
  PairCards,
  ProductDetails,
  ProductFact,
  ProductFaq,
  ProductStory,
  ProductTestimonial,
  RelatedProducts,
  SeoCopy,
} from './productSections';
import styles from './product.module.scss';

const STICKY_TABS = [
  { id: SECTION_IDS.story, label: 'Your story' },
  { id: SECTION_IDS.details, label: 'Details' },
  { id: SECTION_IDS.testimonial, label: 'Reviews' },
  { id: SECTION_IDS.faq, label: 'FAQ' },
  { id: SECTION_IDS.related, label: 'You might like' },
];

// Editor sources of products built from a partner tree, they get the partner dot
const PARTNER_SOURCES = new Set(['ancestry', 'calendar-ancestry', 'pexeso', 'familysearch']);

// Selected SKU as the sticky bar names it, e.g. "13x10 · Black Leather"
const selectionSummary = (product, skuProduct) => {
  if (!product.reducer || !skuProduct) return skuProduct?.name ?? '';

  const formats = product.config.picker?.summaryFormats ?? {};
  return getSkuAttributes(product.reducer, skuProduct.id)
    .map(({ key, value }) => (formats[key] ? formats[key].replace('{value}', value) : value))
    .join(' · ');
};

// The sticky bar shows once the top section's buy button has scrolled above the viewport.
// A scroll listener rather than IntersectionObserver: a jump from below the fold to past
// the button (anchor link, restored scroll) never changes the intersection state.
const useScrolledPast = (ref) => {
  const [scrolledPast, setScrolledPast] = useState(false);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      if (ref.current) setScrolledPast(ref.current.getBoundingClientRect().bottom < 0);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [ref]);

  return scrolledPast;
};

/**
 * Product page for content format 2 (docs/mycanvas-product-page.md). Each section
 * below the top section renders only when its content exists.
 */
const ProductPage = ({ productId, content, breadcrumb }) => {
  const cartContext = useCartContext();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();

  const product = dataContext.getProduct(productId);
  const defaultPrintOnly = product.config.defaultPrintOnly ?? true;
  const hidePrintOnly = product.config.hidePrintOnly ?? false;

  const [configuration, setConfiguration] = useState(() =>
    getInitialPriceData(product, dataContext, defaultPrintOnly),
  );
  const [skuProduct, setSkuProduct] = useState(configuration.product);

  const buyRef = useRef(null);
  const stickyVisible = useScrolledPast(buyRef);

  useEffect(() => {
    tracker.productView(skuProduct);
  }, [skuProduct]);

  const handleOnChangeConfiguration = (data) => {
    if (data.product) {
      setSkuProduct(data.product);
    }
    setConfiguration(data);
  };

  const handleOnAddToCartClick = () => {
    cartContext.addItem(
      configuration.product,
      configuration.product.version,
      configuration.primaryProductData,
      false,
      prepareBundleData(dataContext, configuration),
    );
  };

  const handleOnStartEditorClick = () => {
    if (configuration.bundle || configuration.upsell) {
      editorContext.createItem(
        configuration.product.id,
        configuration.product.version,
        prepareBundleData(dataContext, configuration),
      );
    } else {
      editorContext.removeBundle(configuration.product.id);
    }
  };

  const trackInsideBookClick = (location) =>
    tracker.onCustomEvent('product_inside_book_click', { product: product.id, location });

  const price = formatPrice(configuration.price, product.currency, true);
  const opensEditor = skuProduct && !skuProduct.display;

  const renderBuyAction = (className) => {
    const label = opensEditor ? 'Continue' : 'Add to cart';
    const button = (
      <a
        className={className}
        onClick={opensEditor ? handleOnStartEditorClick : handleOnAddToCartClick}
      >
        {label}
        <ArrowRight size={20} aria-hidden="true" />
      </a>
    );

    if (!opensEditor) {
      return <CartPageLink>{button}</CartPageLink>;
    }

    if (product.config.continueButtonUrl) {
      return (
        <Link
          href={queryString.addParams(product.config.continueButtonUrl, {
            product: product.id,
            ...configuration.config,
          })}
          passHref
          legacyBehavior
        >
          {button}
        </Link>
      );
    }

    return (
      <EditorPageLink
        productId={skuProduct.id}
        version={skuProduct.version}
        quantity={configuration.quantity}
        source={product.config.source}
      >
        {button}
      </EditorPageLink>
    );
  };

  const markupImages = productStillImages(product).map((image) =>
    getCloudinaryUrl({ imageKey: image.key, prefix: 'products/', type: 'big', format: 'jpg' }),
  );

  const relatedProducts = dataContext.getProducts(product.related);
  const stickyTabs = STICKY_TABS.filter(({ id }) => {
    if (id === SECTION_IDS.story) return Boolean(content.story);
    if (id === SECTION_IDS.testimonial) return Boolean(content.testimonial);
    if (id === SECTION_IDS.faq) return Boolean(content.faq?.length);
    if (id === SECTION_IDS.related) return relatedProducts.length > 0;
    return true;
  });

  return (
    <React.Fragment key={product.id}>
      <Head
        title={content.seoTitle ?? content.caption}
        fullTitle={content.seoFullTitle}
        canonicalPath={content.canonicalPath}
        keywords={content.seoKeywords}
        description={content.seoDescription ?? content.shortDescription}
        image={content.seoUrl}
      />

      <ScriptMarkupProduct
        id={product.id}
        url={`${SETTINGS.HOST}${product.url}`}
        name={content.caption}
        description={content.shortDescription}
        images={markupImages}
        prices={product.prices}
        currency={product.currency}
        brand="MyCanvas"
        host={SETTINGS.HOST}
      />

      {content.faq && <ScriptMarkupFaq items={faqContentToItems(content.faq)} />}
      {breadcrumb && <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />}

      <Layout>
        <div className={styles.page}>
          <section className={styles.top}>
            <div className={styles.container}>
              {breadcrumb && (
                <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
                  <ol>
                    {breadcrumb.map((item, index) => (
                      <li key={item.name}>
                        {index > 0 && <ChevronRight size={16} aria-hidden="true" />}
                        {item.url ? (
                          <Link href={item.url}>{item.name}</Link>
                        ) : (
                          <span aria-current="page">{item.name}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              )}

              <div className={styles.topRow}>
                <div className={styles.gallery}>
                  <ProductDetailCarousel images={product.images} />
                </div>

                <div className={styles.buyBox}>
                  <div className={styles.heading}>
                    {(content.eyebrow || content.topBadge) && (
                      <div className={styles.eyebrowRow}>
                        {content.eyebrow && (
                          <p className={styles.partnerLine}>
                            {PARTNER_SOURCES.has(product.config.source) && (
                              <span
                                className={styles.partnerDot}
                                data-source={product.config.source}
                                aria-hidden="true"
                              />
                            )}
                            <span className={styles.eyebrow}>{content.eyebrow}</span>
                          </p>
                        )}
                        {content.topBadge && (
                          <span className={styles.topBadge}>
                            <Icon name={content.topBadge.icon} size={16} />
                            {content.topBadge.label}
                          </span>
                        )}
                      </div>
                    )}
                    <h1 className={styles.title}>{content.caption}</h1>
                  </div>

                  <HtmlContent html={content.description} className={styles.description} />

                  {content.insideBook && (
                    <InsideBookTeaser
                      insideBook={content.insideBook}
                      onClick={() => trackInsideBookClick('buy_box')}
                    />
                  )}

                  {skuProduct ? (
                    <>
                      {product.reducer && (
                        <ProductPicker
                          product={product}
                          skuProduct={skuProduct}
                          defaultPrintOnly={defaultPrintOnly}
                          hidePrintOnly={hidePrintOnly}
                          onChange={handleOnChangeConfiguration}
                        />
                      )}

                      <div className={styles.priceRow}>
                        <div className={styles.price}>
                          <span className={styles.priceLabel}>
                            {product.config.showSkuPriceStartingAt ? 'Price starting at' : 'Price'}
                          </span>
                          <span className={styles.priceValue}>{price}</span>
                        </div>
                        {content.priceNote && (
                          <span className={styles.priceNote}>{content.priceNote}</span>
                        )}
                      </div>

                      <div ref={buyRef}>{renderBuyAction(styles.cta)}</div>
                    </>
                  ) : (
                    <p className={styles.unavailable}>Sorry, this product is not available.</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {skuProduct && (
            <StickyProductBar
              visible={stickyVisible}
              tabs={stickyTabs}
              summary={selectionSummary(product, skuProduct)}
              price={price}
              action={renderBuyAction(styles.stickyCta)}
            />
          )}

          {content.insideBook && (
            <InsideBook insideBook={content.insideBook} onClick={trackInsideBookClick} />
          )}

          {content.story && <ProductStory story={content.story} />}

          <ProductDetails content={content} />

          {content.pairCards && (
            <PairCards cards={content.pairCards} getProduct={dataContext.getProduct} />
          )}

          {content.testimonial && <ProductTestimonial testimonial={content.testimonial} />}

          {content.fact && <ProductFact fact={content.fact} />}

          {content.faq?.length > 0 && (
            <ProductFaq caption={content.caption} intro={content.faqIntro} faq={content.faq} />
          )}

          {relatedProducts.length > 0 && (
            <RelatedProducts title={content.relatedTitle} products={relatedProducts} />
          )}

          {content.extraDescription && <SeoCopy html={content.extraDescription} />}
        </div>
      </Layout>
    </React.Fragment>
  );
};

ProductPage.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object.isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, url: PropTypes.string })),
};

export default ProductPage;
